import React from "react";

import _ from "lodash";

import useAutocomplete, {
    AutocompleteHookType,
    ElementType,
    GetItemsInterface,
    SetItemInterface,
} from "commons/hooks/useAutocomplete";
import useDragAndDropList, { DragAndDropListHook } from "commons/hooks/useDragAndDropList";
import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useSaveActionsContext } from "context/save";

import useProductApi from "hooks/api/private/useProductApi";
import useRelatedProductApi from "hooks/api/private/useRelatedProductApi";

import { createAdvancedListRequestFromFilter, filterEq, filterLike } from "utils/utils";

type ProductForm = {
    id?: string;
    product_id: string;
    related_product_id: string;
    graphical_order: number;
    related_product_name: string;
    toDelete: boolean;
    toCreate: boolean;
    toUpdate: boolean;
};

type RelatedProductsForm = {
    products: Array<ProductForm>;
};

export type FormProductsHook = {
    onDeleteIconClick: (productId: string) => void;
    productAutocompleteHook: AutocompleteHookType<string>;
    dragAndDropHook: DragAndDropListHook<unknown>;
    formHook: FormHookType<RelatedProductsForm>;
};

function useRelatedProductsForm({ productId }: { productId: string }): FormProductsHook {
    const { getAllProduct } = useProductApi();
    const {
        createRelatedProduct,
        getAllRelatedProduct,
        deleteRelatedProduct,
        updateRelatedProduct,
    } = useRelatedProductApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                products: {
                    type: "objectArray",
                    of: {
                        type: "object",
                        properties: {},
                    },
                },
            },
        };
    }, []);

    const initialValues: RelatedProductsForm = React.useMemo(() => {
        return {
            products: [],
        };
    }, []);

    const formHook = useForm<RelatedProductsForm>(schema, initialValues);

    const { setForm, form } = formHook;

    const getProducts: GetItemsInterface<string> = React.useCallback(
        (setItems: SetItemInterface<string>, value: string) => {
            const [apiCall, abortController] = getAllProduct(
                createAdvancedListRequestFromFilter(filterLike("products.name", value)),
            );

            apiCall.then((response) => {
                setItems(() => {
                    const finalItems: ElementType<string>[] = [];

                    response.content.forEach((product) => {
                        if (product.id !== productId) {
                            finalItems.push({
                                value: product.id,
                                label: `${product.name} | ${product.category_name}`,
                            });
                        }
                    });

                    return finalItems;
                });
            });

            return abortController;
        },
        [getAllProduct, productId],
    );

    const onProductsValueChange = React.useCallback(
        (element: ElementType<string> | undefined) => {
            if (element !== undefined) {
                setForm((prevForm) => {
                    const newForm = _.cloneDeep(prevForm);

                    // to avoid adding the same element twice
                    const elementIsNotPresent: boolean = newForm.products.every(
                        (product) => product.related_product_id !== element.value,
                    );

                    // check if the lenght of the shown product is less than 4
                    const lowerThanFourElements: () => boolean = () => {
                        const finalValue = newForm.products.filter((product) => {
                            if (!product.toDelete) {
                                return product;
                            }

                            return null;
                        });

                        return finalValue.length < 4;
                    };

                    if (elementIsNotPresent && lowerThanFourElements()) {
                        newForm.products.push({
                            product_id: productId,
                            related_product_id: element.value,
                            graphical_order: (newForm.products.length + 1) * 10,
                            related_product_name: element.label,
                            toDelete: false,
                            toCreate: true,
                            toUpdate: false,
                        });
                    }
                    return newForm;
                });
            }
        },
        [setForm, productId],
    );

    const productAutocompleteHook = useAutocomplete(getProducts, onProductsValueChange, "", true);

    const onDeleteIconClick = React.useCallback(
        (relatedProductId: string) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const finalProducts = newForm.products.map((product) => {
                    if (product.related_product_id === relatedProductId) {
                        return { ...product, toDelete: true };
                    }

                    return product;
                });

                const finalForm: RelatedProductsForm = { products: finalProducts };

                return finalForm;
            });
        },
        [setForm],
    );

    React.useEffect(() => {
        const [apiCall] = getAllRelatedProduct(
            createAdvancedListRequestFromFilter(
                filterEq("products_related.product_id", productId),
                "products_related.graphical_order,ASC",
            ),
        );

        apiCall.then((res) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.products = res.content.map((product) => {
                    return {
                        id: product.id,
                        product_id: product.product_id,
                        related_product_id: product.related_product_id,
                        graphical_order: product.graphical_order,
                        related_product_name: `${product.name} | ${product.category_name}`,
                        toDelete: false,
                        toCreate: false,
                        toUpdate: false,
                    };
                });

                return newForm;
            });
        });
    }, [getAllRelatedProduct, setForm, productId]);

    React.useEffect(() => {
        addSaveAction("useRelatedProductsForm", () => {
            const promises: Array<Promise<unknown>> = [];

            form.products.forEach((product) => {
                if (product.toCreate === true) {
                    const [apiCall] = createRelatedProduct({
                        product_id: product.product_id,
                        related_product_id: product.related_product_id,
                        graphical_order: product.graphical_order,
                    });

                    promises.push(apiCall);

                    apiCall.then((response) => {
                        setForm((prevForm) => {
                            const newForm = _.cloneDeep(prevForm);

                            const index = newForm.products.findIndex(
                                (item) => item.related_product_id === product.related_product_id,
                            );

                            if (index !== -1) {
                                newForm.products[index].id = response.id;
                                newForm.products[index].toCreate = false;
                            }

                            return newForm;
                        });
                    });
                }

                if (product.toDelete === true && product.id) {
                    const [apiCall] = deleteRelatedProduct({ id: product.id });

                    promises.push(apiCall);

                    apiCall.then(() => {
                        setForm((prevForm) => {
                            const newForm = _.cloneDeep(prevForm);

                            const index = newForm.products.findIndex(
                                (item) => item.related_product_id === product.related_product_id,
                            );

                            if (index !== -1) {
                                newForm.products.splice(index, 1);
                            }

                            return newForm;
                        });
                    });
                }

                if (product.toUpdate === true && product.id) {
                    const [apiCall] = updateRelatedProduct(
                        { id: product.id },
                        {
                            product_id: product.product_id,
                            related_product_id: product.related_product_id,
                            graphical_order: product.graphical_order,
                        },
                    );

                    promises.push(apiCall);
                }
            });

            return Promise.all(promises);
        });
    }, [
        addSaveAction,
        createRelatedProduct,
        deleteRelatedProduct,
        updateRelatedProduct,
        setForm,
        form,
    ]);

    const onUpdateList = React.useCallback(
        (newList) => {
            setForm((prevList) => {
                const tmpList = _.cloneDeep(prevList);

                tmpList.products = newList;

                tmpList.products.forEach((singleRow, index) => {
                    tmpList.products[index].graphical_order = (index + 1) * 10;
                    tmpList.products[index].toUpdate = true;
                });

                return tmpList;
            });
        },
        [setForm],
    );

    const dragAndDropHook = useDragAndDropList({ list: form.products, onUpdateList });

    return React.useMemo(
        (): FormProductsHook => ({
            onDeleteIconClick,
            productAutocompleteHook,
            dragAndDropHook,
            formHook,
        }),
        [onDeleteIconClick, productAutocompleteHook, dragAndDropHook, formHook],
    );
}

export default useRelatedProductsForm;
